const normalizePath = (path: string) => {
    const normalized = path.replace(/\/+$/, '');
    return normalized || '/';
};

const getHashTarget = (hash: string) => {
    const rawId = hash.replace(/^#/, '');
    if (!rawId) return null;

    let id = rawId;
    try {
        id = decodeURIComponent(rawId);
    } catch {
        id = rawId;
    }

    return document.getElementById(id);
};

const getSamePageHashTarget = (link: HTMLAnchorElement) => {
    if (!link.hash || link.hash === '#') return null;

    const url = new URL(link.href, window.location.href);
    const samePage =
        url.origin === window.location.origin &&
        normalizePath(url.pathname) === normalizePath(window.location.pathname) &&
        (url.search === window.location.search || url.search === '');

    if (!samePage) return null;

    const target = getHashTarget(url.hash);
    return target ? { target, hash: url.hash } : null;
};

const smoothScrollToTarget = (target: HTMLElement) => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollMarginTop = parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - scrollMarginTop;

    window.scrollTo({
        top,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
};

const initializeNavigationMenus = () => {
    const navMenus = document.querySelectorAll('.stgb-nav-menu') as NodeListOf<HTMLElement>;

    navMenus.forEach((navWrapper) => {
        const orientation = navWrapper.getAttribute('data-orientation');
        const navInner = navWrapper.querySelector('.stgb-nav-menu__inner') as HTMLElement | null;
        const overlay = navWrapper.querySelector('.stgb-nav-menu__overlay') as HTMLElement | null;
        const toggleBtn = navWrapper.querySelector('.stgb-nav-menu__toggle');
        const closeBtn = navWrapper.querySelector('.stgb-nav-menu__close');

        if (!navInner) return;

        // The overlay is portaled to <body> in mobile mode, so responsive and
        // open-state classes must be mirrored onto both elements.
        const setState = (className: string, enabled: boolean) => {
            navWrapper.classList.toggle(className, enabled);
            if (overlay) overlay.classList.toggle(className, enabled);
        };

        const closeMenu = () => {
            setState('is-open', false);
            if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
        };

        navInner.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
            link.addEventListener('click', (event) => {
                if (
                    event.defaultPrevented ||
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey ||
                    (link.target && link.target !== '_self')
                ) {
                    return;
                }

                const anchorTarget = getSamePageHashTarget(link);
                if (anchorTarget) {
                    event.preventDefault();
                    closeMenu();

                    if (window.location.hash !== anchorTarget.hash) {
                        window.history.pushState(null, '', anchorTarget.hash);
                    }

                    requestAnimationFrame(() => smoothScrollToTarget(anchorTarget.target));
                    return;
                }

                if (navWrapper.classList.contains('is-open')) {
                    closeMenu();
                }
            });
        });

        if (orientation === 'vertical') {
            setState('is-mobile-menu', false);
            setState('is-open', false);
            setState('is-initialized', true);
            return;
        }

        const parent = navWrapper.parentElement;
        if (!parent) return;

        const buffer = 40;
        let requiredDesktopWidth = 0;

        const openMenu = () => {
            setState('is-open', true);
            if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
            document.body.style.overflow = 'hidden';
        };

        const inheritedTypeProperties = [
            'font-family',
            'font-size',
            'font-style',
            'font-weight',
            'letter-spacing',
            'text-decoration-line',
        ];

        const detachOverlay = () => {
            if (!overlay || overlay.parentElement === document.body) return;

            const navStyle = window.getComputedStyle(navWrapper);
            inheritedTypeProperties.forEach((property) => {
                overlay.style.setProperty(property, navStyle.getPropertyValue(property));
            });

            document.body.appendChild(overlay);
        };

        const reattachOverlay = () => {
            if (!overlay || overlay.parentElement !== document.body) return;

            inheritedTypeProperties.forEach((property) => {
                overlay.style.removeProperty(property);
            });

            navWrapper.appendChild(overlay);
        };

        if (toggleBtn) toggleBtn.addEventListener('click', openMenu);
        if (closeBtn) closeBtn.addEventListener('click', closeMenu);

        const getOuterWidth = (element: Element): number => {
            const style = window.getComputedStyle(element);
            if (style.display === 'none' || style.position === 'absolute' || style.position === 'fixed') {
                return 0;
            }

            const marginLeft = parseFloat(style.marginLeft) || 0;
            const marginRight = parseFloat(style.marginRight) || 0;
            return (element as HTMLElement).getBoundingClientRect().width + marginLeft + marginRight;
        };

        const calculateRequiredWidth = () => {
            let totalSiblingWidth = 0;
            let visibleSiblings = 0;

            Array.from(parent.children).forEach((child) => {
                if (child === navWrapper) return;

                const width = getOuterWidth(child);
                if (width > 0) {
                    totalSiblingWidth += width;
                    visibleSiblings++;
                }
            });

            const parentStyle = window.getComputedStyle(parent);
            const gap = parseFloat(parentStyle.columnGap) || parseFloat(parentStyle.gap) || 0;
            const totalGapWidth = gap * visibleSiblings;

            if (window.getComputedStyle(navInner).display !== 'none') {
                return totalSiblingWidth + getOuterWidth(navInner) + totalGapWidth + buffer;
            }

            return requiredDesktopWidth;
        };

        const observer = new ResizeObserver((entries) => {
            const availableWidth = entries[0].contentRect.width;

            if (!navWrapper.classList.contains('is-mobile-menu')) {
                requiredDesktopWidth = calculateRequiredWidth();
            }

            if (availableWidth < requiredDesktopWidth && requiredDesktopWidth > 0) {
                setState('is-mobile-menu', true);
                detachOverlay();
            } else {
                if (navWrapper.classList.contains('is-open')) {
                    closeMenu();
                }
                setState('is-mobile-menu', false);
                reattachOverlay();
            }

            if (!navWrapper.classList.contains('is-initialized')) {
                requestAnimationFrame(() => setState('is-initialized', true));
            }
        });

        observer.observe(parent);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeNavigationMenus);
} else {
    initializeNavigationMenus();
}
